const SESSION_PREFETCH_TTL = 15_000

export type SessionPrefetchMeta = {
  limit: number
  cursor?: string
  complete: boolean
  at: number
}

export function shouldSkipSessionPrefetch(input: {
  message: boolean
  info?: SessionPrefetchMeta
  chunk: number
  now?: number
}) {
  if (input.message) {
    if (!input.info || input.info.complete || input.info.limit > input.chunk) return true
  } else if (!input.info) {
    return false
  }
  return (input.now ?? Date.now()) - input.info.at < SESSION_PREFETCH_TTL
}

export function createSessionPrefetchStore(input: {
  scope: () => string
  maxEntries?: number
}) {
  const cache = new Map<string, SessionPrefetchMeta>()
  const inflight = new Map<string, Promise<SessionPrefetchMeta | undefined>>()
  const controllers = new Map<string, AbortController>()
  const revisions = new Map<string, number>()
  const maxEntries = input.maxEntries ?? 200
  let nextRevision = 0
  let disposed = false

  const key = (directory: string, sessionID: string) =>
    `${input.scope()}\n${directory}\n${sessionID}`
  const invalidate = (id: string) => {
    controllers.get(id)?.abort()
    cache.delete(id)
    inflight.delete(id)
    controllers.delete(id)
    revisions.delete(id)
  }
  const prune = () => {
    while (cache.size > maxEntries) {
      const id = cache.keys().next().value
      if (!id) break
      invalidate(id)
    }
  }

  return {
    get(directory: string, sessionID: string) {
      const id = key(directory, sessionID)
      const value = cache.get(id)
      if (!value) return undefined
      cache.delete(id)
      cache.set(id, value)
      return value
    },
    pending(directory: string, sessionID: string) {
      return inflight.get(key(directory, sessionID))
    },
    set(value: Omit<SessionPrefetchMeta, "at"> & {
      directory: string
      sessionID: string
      at?: number
    }) {
      if (disposed) return
      const id = key(value.directory, value.sessionID)
      cache.delete(id)
      cache.set(id, {
        limit: value.limit,
        cursor: value.cursor,
        complete: value.complete,
        at: value.at ?? Date.now(),
      })
      prune()
    },
    run(value: {
      directory: string
      sessionID: string
      task: (revision: number, signal: AbortSignal) => Promise<SessionPrefetchMeta | undefined>
    }) {
      if (disposed) return Promise.resolve(undefined)
      const id = key(value.directory, value.sessionID)
      const current = inflight.get(id)
      if (current) return current
      while (inflight.size >= maxEntries) {
        const stale = inflight.keys().next().value
        if (!stale) break
        invalidate(stale)
      }
      const revision = ++nextRevision
      const controller = new AbortController()
      revisions.set(id, revision)
      controllers.set(id, controller)
      const request = value.task(revision, controller.signal).finally(() => {
        if (inflight.get(id) === request) inflight.delete(id)
        if (controllers.get(id) === controller) controllers.delete(id)
        if (revisions.get(id) === revision) revisions.delete(id)
      })
      inflight.set(id, request)
      return request
    },
    current(directory: string, sessionID: string, revision: number) {
      return !disposed && revisions.get(key(directory, sessionID)) === revision
    },
    clear(directory: string, sessionIDs: Iterable<string>) {
      for (const sessionID of sessionIDs) {
        if (sessionID) invalidate(key(directory, sessionID))
      }
    },
    clearDirectory(directory: string) {
      const prefix = `${input.scope()}\n${directory}\n`
      const keys = new Set([...cache.keys(), ...inflight.keys(), ...revisions.keys()])
      for (const id of keys) if (id.startsWith(prefix)) invalidate(id)
    },
    sizes() {
      return { cache: cache.size, inflight: inflight.size, revisions: revisions.size, controllers: controllers.size }
    },
    dispose() {
      disposed = true
      for (const controller of controllers.values()) controller.abort()
      cache.clear()
      inflight.clear()
      controllers.clear()
      revisions.clear()
    },
  }
}

export type SessionPrefetchStore = ReturnType<typeof createSessionPrefetchStore>
